import { useLayoutEffect, useRef, type ReactNode } from "react";
import type { CanvasCamera } from "@/lib/canvas-camera";
import { nativeCanvasLayout, type CanvasBounds } from "@/lib/native-canvas";
import type { CanvasView } from "@/lib/viewport";

/** Safari moves this overflow layer itself. Scrolling never rewrites its transform. */
export function NativeCanvasStage({ camera, bounds, className, children }: {
  camera: CanvasCamera; bounds: CanvasBounds; className: string; children: ReactNode;
}) {
  const viewport = useRef<HTMLDivElement>(null), plane = useRef<HTMLDivElement>(null), artwork = useRef<HTMLDivElement>(null);
  const currentBounds = useRef(bounds);
  currentBounds.current = bounds;
  const refresh = useRef<() => void>(() => {});
  useLayoutEffect(() => {
    const el = viewport.current!, content = plane.current!, stage = artwork.current!;
    let origin = { x: 0, y: 0 }, lastNativeView: CanvasView | null = null;
    const same = (a: CanvasView, b: CanvasView | null) => b && a.zoom === b.zoom && a.pan.x === b.pan.x && a.pan.y === b.pan.y;
    const position = () => {
      if (!el.clientWidth || !el.clientHeight) return;
      const view = camera.getView();
      const next = nativeCanvasLayout(view, currentBounds.current, { width: el.clientWidth, height: el.clientHeight });
      origin = next.origin;
      content.style.width = `${next.width}px`; content.style.height = `${next.height}px`;
      stage.style.transform = `translate(${origin.x}px,${origin.y}px) scale(${view.zoom})`;
      // Only explicit fit/zoom/page/layout changes may reposition the scroller.
      // Writing scrollLeft/Top during native momentum can stop Safari's glide.
      if (Math.abs(el.scrollLeft - next.scroll.x) > 0.5) el.scrollLeft = next.scroll.x;
      if (Math.abs(el.scrollTop - next.scroll.y) > 0.5) el.scrollTop = next.scroll.y;
      lastNativeView = { zoom: view.zoom, pan: { x: origin.x - el.scrollLeft, y: origin.y - el.scrollTop } };
    };
    refresh.current = position;
    position();
    const scroll = () => {
      if (!el.clientWidth || !el.clientHeight) return;
      const next = { zoom: camera.getView().zoom, pan: { x: origin.x - el.scrollLeft, y: origin.y - el.scrollTop } };
      lastNativeView = next;
      camera.setView(next); // Passive bookkeeping for pins, hit testing and saved views.
    };
    const unsubscribe = camera.subscribe(() => { if (!same(camera.getView(), lastNativeView)) position(); });
    const resize = new ResizeObserver(position);
    resize.observe(el);
    el.addEventListener("scroll", scroll, { passive: true });
    return () => { el.removeEventListener("scroll", scroll); resize.disconnect(); unsubscribe(); refresh.current = () => {}; };
  }, [camera]);
  useLayoutEffect(() => { refresh.current(); }, [bounds.left, bounds.top, bounds.right, bounds.bottom]);
  return <div className="canvas-native-scroll" ref={viewport}>
    <div className="canvas-native-plane" ref={plane}>
      <div className={className} ref={artwork}>{children}</div>
    </div>
  </div>;
}
